import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  writeBatch,
  serverTimestamp,
  where,
  Timestamp,
  updateDoc,
  arrayUnion,
  arrayRemove,
  startAfter,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions, storage } from './firebase';
import { registerParentChatFcm, subscribeForegroundChatMessages } from './fcmRegister';
import { useSwipeable } from 'react-swipeable';
import './ParentChat.css';

const EMPTY_DM_READS = {};
const FORUM_PAGE = 80;
const DM_PAGE = 100;
const SCROLL_NEAR_BOTTOM_PX = 100;
const GROUPING_MAX_GAP_MS = 5 * 60 * 1000;
const MAX_CHAT_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_MENTION_IDS = 50;

function canonicalConversationId(uidA, uidB) {
  return uidA < uidB ? `${uidA}_${uidB}` : `${uidB}_${uidA}`;
}

function previewText(text, max = 80) {
  const t = (text || '').trim().replace(/\s+/g, ' ');
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

function previewForList(text, imageUrl) {
  const t = (text || '').trim();
  if (imageUrl && !t) return '📷 Photo';
  if (imageUrl && t) return previewText(t, 80);
  return previewText(t, 80);
}

function useIsNarrow(breakpoint = 768) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const fn = () => setNarrow(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, [breakpoint]);
  return narrow;
}

function replyLinePreview(parentMsg) {
  if (!parentMsg) return 'Earlier message';
  const name = parentMsg.authorName || 'Parent';
  const t = (parentMsg.text || '').trim();
  if (parentMsg.imageUrl && !t) return `${name} · Photo`;
  return `${name} · ${previewText(t || '…', 72)}`;
}

function initialsFromName(name) {
  const p = (name || 'P').trim().split(/\s+/).filter(Boolean);
  if (p.length >= 2) return (p[0][0] + p[1][0]).toUpperCase();
  return (p[0] || 'P').slice(0, 2).toUpperCase();
}

function messageDate(m) {
  if (!m?.createdAt?.toDate) return null;
  return m.createdAt.toDate();
}

function messageTimeMs(m) {
  const d = messageDate(m);
  return d ? d.getTime() : 0;
}

function sameCalendarDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dateSeparatorLabel(d) {
  const now = new Date();
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const d0 = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((today0 - d0) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function msgTimeShort(m) {
  const d = messageDate(m);
  if (!d) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function messagesGroupTogether(prev, cur) {
  if (!prev || !cur) return false;
  const prevOfficial = prev.authorId === 'admin' || prev.isOfficial;
  const curOfficial = cur.authorId === 'admin' || cur.isOfficial;
  if (prevOfficial || curOfficial) return false;
  if (prev.authorId !== cur.authorId) return false;
  const tp = messageTimeMs(prev);
  const tc = messageTimeMs(cur);
  if (!tp || !tc) return false;
  return tc - tp <= GROUPING_MAX_GAP_MS;
}

function mergeMessagesByIdAsc(olderAsc, liveAsc) {
  const map = new Map();
  for (const m of olderAsc) {
    if (m?.id) map.set(m.id, m);
  }
  for (const m of liveAsc) {
    if (m?.id) map.set(m.id, m);
  }
  return Array.from(map.values()).sort((a, b) => messageTimeMs(a) - messageTimeMs(b));
}

/** WhatsApp-style strip above composer: accent bar, optional thumb, name, snippet, dismiss */
function ReplyComposerPreview({ authorLabel, quotedMessage, onDismiss }) {
  const hasImg = !!(quotedMessage?.imageUrl && String(quotedMessage.imageUrl).trim());
  const snippet = !quotedMessage
    ? '…'
    : hasImg && !(quotedMessage.text || '').trim()
      ? 'Photo'
      : previewText((quotedMessage.text || '').trim() || '…', 160);
  return (
    <div className="parent-chat-reply-wa">
      <div className="parent-chat-reply-wa-accent" aria-hidden />
      {hasImg ? (
        <img src={quotedMessage.imageUrl} alt="" className="parent-chat-reply-wa-thumb" />
      ) : null}
      <div className="parent-chat-reply-wa-main">
        <div className="parent-chat-reply-wa-name">{authorLabel}</div>
        <div className="parent-chat-reply-wa-snippet">{snippet}</div>
      </div>
      <button type="button" className="parent-chat-reply-wa-x" onClick={onDismiss} aria-label="Cancel reply">
        ×
      </button>
    </div>
  );
}

function ForumMessageRow({
  m,
  uid,
  displayName,
  myPhotoUrl,
  threadMessages,
  isNarrow,
  actionOpen,
  onDesktopActivate,
  onReply,
  onRequestDelete,
  onMobileMenuOpen,
  renderBubbleContent,
  renderReplyContext,
  timeShort,
  groupWithPrev,
  groupWithNext,
}) {
  const mine = m.authorId === uid;
  const official = m.authorId === 'admin' || m.isOfficial;
  const longPressTimerRef = useRef(null);

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearLongPress(), [clearLongPress]);

  const swipeHandlers = useSwipeable({
    trackTouch: isNarrow,
    trackMouse: false,
    delta: 45,
    preventScrollOnSwipe: true,
    onTouchStartOrOnMouseDown: ({ event }) => {
      if (!isNarrow || !onMobileMenuOpen) return;
      const isTouch = 'touches' in event && event.touches && event.touches.length > 0;
      if (!isTouch) return;
      clearLongPress();
      longPressTimerRef.current = window.setTimeout(() => {
        longPressTimerRef.current = null;
        onMobileMenuOpen();
        if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(12);
      }, 480);
    },
    onSwiping: () => {
      clearLongPress();
    },
    onTouchEndOrOnMouseUp: () => {
      clearLongPress();
    },
    onSwipedRight: () => {
      clearLongPress();
      onReply();
    },
    onSwipedLeft: () => {
      clearLongPress();
      if (!mine || official) return;
      onRequestDelete();
    },
  });

  const handleDesktopClick = (e) => {
    if (isNarrow) return;
    if (e.target.closest('a[href]')) return;
    e.stopPropagation();
    onDesktopActivate();
  };

  const rowClass = `parent-chat-msg-row parent-chat-msg-interactive ${official ? 'parent-chat-msg-row--official' : ''} ${mine && !official ? 'is-mine' : ''} ${actionOpen && !isNarrow ? 'is-actions-open' : ''} ${!official && groupWithPrev ? 'parent-chat-msg-row--stacked' : ''}`;

  const blockContextMenu = (e) => {
    if (isNarrow) e.preventDefault();
  };

  if (official) {
    return (
      <div {...swipeHandlers} className="parent-chat-msg-swipe-wrap">
        <div className={rowClass} onClick={handleDesktopClick} onContextMenu={blockContextMenu}>
          <div className="parent-chat-msg-col parent-chat-msg-col--official">
            <div className="parent-chat-msg-label">
              <span className="parent-chat-official-badge">Team</span>
              <span className="parent-chat-msg-author">{m.authorName || 'Team Admin'}</span>
              {timeShort ? <span className="parent-chat-time-inline">{timeShort}</span> : null}
            </div>
            {renderReplyContext(m, threadMessages)}
            {renderBubbleContent(m, false, true)}
            <div className="parent-chat-msg-actions parent-chat-msg-actions--popover" role="group" aria-label="Message actions">
              <button type="button" className="parent-chat-reply-btn" onClick={(e) => { e.stopPropagation(); onReply(); }}>
                Reply
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...swipeHandlers} className="parent-chat-msg-swipe-wrap">
      <div className={rowClass} onClick={handleDesktopClick} onContextMenu={blockContextMenu}>
        {!mine && !groupWithPrev && (
          <div className="parent-chat-avatar" aria-hidden>
            {m.authorPhotoUrl ? (
              <img src={m.authorPhotoUrl} alt="" />
            ) : (
              <span className="parent-chat-avatar-placeholder">{initialsFromName(m.authorName)}</span>
            )}
          </div>
        )}
        {!mine && groupWithPrev ? <div className="parent-chat-avatar parent-chat-avatar--spacer" aria-hidden /> : null}
        <div className="parent-chat-msg-col">
          {!mine && !groupWithPrev && (
            <div className="parent-chat-msg-label">
              <span className="parent-chat-msg-author">{m.authorName || 'Parent'}</span>
            </div>
          )}
          {renderReplyContext(m, threadMessages)}
          {renderBubbleContent(m, mine, false)}
          {!groupWithNext && timeShort ? <div className="parent-chat-msg-foot-time">{timeShort}</div> : null}
          <div className="parent-chat-msg-actions parent-chat-msg-actions--popover" role="group" aria-label="Message actions">
            <button
              type="button"
              className="parent-chat-reply-btn"
              onClick={(e) => {
                e.stopPropagation();
                onReply();
              }}
            >
              Reply
            </button>
            {mine && (
              <button
                type="button"
                className="parent-chat-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestDelete();
                }}
              >
                Delete
              </button>
            )}
          </div>
        </div>
        {mine && !groupWithPrev && (
          <div className="parent-chat-avatar parent-chat-avatar--mine" aria-hidden>
            {myPhotoUrl ? (
              <img src={myPhotoUrl} alt="" />
            ) : (
              <span className="parent-chat-avatar-placeholder">{initialsFromName(displayName)}</span>
            )}
          </div>
        )}
        {mine && groupWithPrev ? <div className="parent-chat-avatar parent-chat-avatar--mine parent-chat-avatar--spacer" aria-hidden /> : null}
      </div>
    </div>
  );
}

function DmMessageRow({
  m,
  uid,
  displayName,
  myPhotoUrl,
  dmMessages,
  isNarrow,
  actionOpen,
  onDesktopActivate,
  onReply,
  onRequestDelete,
  onMobileMenuOpen,
  renderBubbleContent,
  renderReplyContext,
  timeShort,
  groupWithPrev,
  groupWithNext,
}) {
  const mine = m.authorId === uid;
  const longPressTimerRef = useRef(null);

  const clearLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearLongPress(), [clearLongPress]);

  const swipeHandlers = useSwipeable({
    trackTouch: isNarrow,
    trackMouse: false,
    delta: 45,
    preventScrollOnSwipe: true,
    onTouchStartOrOnMouseDown: ({ event }) => {
      if (!isNarrow || !onMobileMenuOpen) return;
      const isTouch = 'touches' in event && event.touches && event.touches.length > 0;
      if (!isTouch) return;
      clearLongPress();
      longPressTimerRef.current = window.setTimeout(() => {
        longPressTimerRef.current = null;
        onMobileMenuOpen();
        if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(12);
      }, 480);
    },
    onSwiping: () => {
      clearLongPress();
    },
    onTouchEndOrOnMouseUp: () => {
      clearLongPress();
    },
    onSwipedRight: () => {
      clearLongPress();
      onReply();
    },
    onSwipedLeft: () => {
      clearLongPress();
      if (!mine) return;
      onRequestDelete();
    },
  });

  const handleDesktopClick = (e) => {
    if (isNarrow) return;
    if (e.target.closest('a[href]')) return;
    e.stopPropagation();
    onDesktopActivate();
  };

  const rowClass = `parent-chat-msg-row parent-chat-msg-interactive ${mine ? 'is-mine' : ''} ${actionOpen && !isNarrow ? 'is-actions-open' : ''} ${groupWithPrev ? 'parent-chat-msg-row--stacked' : ''}`;
  const blockContextMenu = (e) => {
    if (isNarrow) e.preventDefault();
  };

  return (
    <div {...swipeHandlers} className="parent-chat-msg-swipe-wrap">
      <div className={rowClass} onClick={handleDesktopClick} onContextMenu={blockContextMenu}>
        {!mine && !groupWithPrev && (
          <div className="parent-chat-avatar" aria-hidden>
            {m.authorPhotoUrl ? (
              <img src={m.authorPhotoUrl} alt="" />
            ) : (
              <span className="parent-chat-avatar-placeholder">{initialsFromName(m.authorName)}</span>
            )}
          </div>
        )}
        {!mine && groupWithPrev ? <div className="parent-chat-avatar parent-chat-avatar--spacer" aria-hidden /> : null}
        <div className="parent-chat-msg-col">
          {!mine && !groupWithPrev && (
            <div className="parent-chat-msg-label">
              <span className="parent-chat-msg-author">{m.authorName || 'Parent'}</span>
            </div>
          )}
          {renderReplyContext(m, dmMessages)}
          {renderBubbleContent(m, mine, false)}
          {!groupWithNext && timeShort ? <div className="parent-chat-msg-foot-time">{timeShort}</div> : null}
          <div className="parent-chat-msg-actions parent-chat-msg-actions--popover" role="group" aria-label="Message actions">
            <button
              type="button"
              className="parent-chat-reply-btn"
              onClick={(e) => {
                e.stopPropagation();
                onReply();
              }}
            >
              Reply
            </button>
            {mine && (
              <button
                type="button"
                className="parent-chat-delete"
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestDelete();
                }}
              >
                Delete
              </button>
            )}
          </div>
        </div>
        {mine && !groupWithPrev && (
          <div className="parent-chat-avatar parent-chat-avatar--mine" aria-hidden>
            {myPhotoUrl ? (
              <img src={myPhotoUrl} alt="" />
            ) : (
              <span className="parent-chat-avatar-placeholder">{initialsFromName(displayName)}</span>
            )}
          </div>
        )}
        {mine && groupWithPrev ? <div className="parent-chat-avatar parent-chat-avatar--mine parent-chat-avatar--spacer" aria-hidden /> : null}
      </div>
    </div>
  );
}

export default function ParentChat({ displayName: displayNameProp, layout = 'embedded', onProfilePhotoUpdated }) {
  const user = auth.currentUser;
  const uid = user?.uid;
  const isNarrow = useIsNarrow(768);
  const [subTab, setSubTab] = useState('discussions');
  const [discussionsTeamWide, setDiscussionsTeamWide] = useState([]);
  const [discussionsMember, setDiscussionsMember] = useState([]);
  const [activeDiscussionId, setActiveDiscussionId] = useState(null);
  const [discussionLiveWindow, setDiscussionLiveWindow] = useState([]);
  const [discussionOlderFetched, setDiscussionOlderFetched] = useState([]);
  const [discussionLoadingOlder, setDiscussionLoadingOlder] = useState(false);
  const [discussionNoMoreOlder, setDiscussionNoMoreOlder] = useState(false);
  const [showForumJump, setShowForumJump] = useState(false);
  const [discussionLoading, setDiscussionLoading] = useState(true);
  const [discussionText, setDiscussionText] = useState('');
  const [discussionSending, setDiscussionSending] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [chatState, setChatState] = useState(null);
  const [chatStateLoading, setChatStateLoading] = useState(true);
  const [myPhotoUrl, setMyPhotoUrl] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [discussionImageFile, setDiscussionImageFile] = useState(null);
  const [discussionImagePreview, setDiscussionImagePreview] = useState(null);
  const [dmImageFile, setDmImageFile] = useState(null);
  const [dmImagePreview, setDmImagePreview] = useState(null);
  const forumEndRef = useRef(null);
  const forumStreamRef = useRef(null);
  const forumInputRef = useRef(null);
  const dmInputRef = useRef(null);
  const dmStreamRef = useRef(null);
  const dmEndRef = useRef(null);
  const forumNearBottomRef = useRef(true);
  const dmNearBottomRef = useRef(true);
  const forumForceScrollRef = useRef(false);
  const dmForceScrollRef = useRef(false);
  const forumTailIdRef = useRef(null);
  const dmTailIdRef = useRef(null);
  const discussionLiveOldestSnapRef = useRef(null);
  const discussionPageCursorRef = useRef(null);
  const dmLiveOldestSnapRef = useRef(null);
  const dmPageCursorRef = useRef(null);
  const toastTimerRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [convLoading, setConvLoading] = useState(true);
  const [activeConvId, setActiveConvId] = useState(null);
  const [dmLiveWindow, setDmLiveWindow] = useState([]);
  const [dmOlderFetched, setDmOlderFetched] = useState([]);
  const [dmLoadingOlder, setDmLoadingOlder] = useState(false);
  const [dmNoMoreOlder, setDmNoMoreOlder] = useState(false);
  const [showDmJump, setShowDmJump] = useState(false);
  const [dmLoading, setDmLoading] = useState(false);
  const [dmText, setDmText] = useState('');
  const [dmSending, setDmSending] = useState(false);
  const [showNewDm, setShowNewDm] = useState(false);
  /** @type {{ id: string, authorName: string } | null} */
  const [replyToDiscussion, setReplyToDiscussion] = useState(null);
  /** @type {{ id: string, authorName: string } | null} */
  const [replyToDm, setReplyToDm] = useState(null);
  const [discussionActionId, setDiscussionActionId] = useState(null);
  const [dmActionId, setDmActionId] = useState(null);
  /** @type {{ type: 'discussion' | 'dm', msg: object } | null} */
  const [mobileMsgMenu, setMobileMsgMenu] = useState(null);
  const [newDmPickIds, setNewDmPickIds] = useState([]);
  const [dmDirectorySearch, setDmDirectorySearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [mentionedUserIds, setMentionedUserIds] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [pushBusy, setPushBusy] = useState(false);
  const [pushStatus, setPushStatus] = useState('');
  const [fcmToast, setFcmToast] = useState(null);
  const [dmArchivedSectionOpen, setDmArchivedSectionOpen] = useState(false);
  const [appToast, setAppToast] = useState(null);
  /** @type {{ channel: 'discussion' | 'dm', msg: object } | null} */
  const [pendingDelete, setPendingDelete] = useState(null);
  const [discussionMentionInfoOpen, setDiscussionMentionInfoOpen] = useState(false);
  const [dmGroupMentionInfoOpen, setDmGroupMentionInfoOpen] = useState(false);
  const [profileBarExpanded, setProfileBarExpanded] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 768
  );
  const mentionPanelRef = useRef(null);

  const discussionMessages = useMemo(
    () => mergeMessagesByIdAsc(discussionOlderFetched, discussionLiveWindow),
    [discussionOlderFetched, discussionLiveWindow]
  );
  const dmMessages = useMemo(() => mergeMessagesByIdAsc(dmOlderFetched, dmLiveWindow), [dmOlderFetched, dmLiveWindow]);

  const displayName =
    (displayNameProp || '').trim() ||
    user?.displayName?.trim() ||
    (user?.email ? user.email.split('@')[0] : '') ||
    'Parent';

  const showToast = useCallback((message) => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    setAppToast({ message });
    toastTimerRef.current = window.setTimeout(() => {
      setAppToast(null);
      toastTimerRef.current = null;
    }, 5000);
  }, []);

  useEffect(() => {
    if (!isNarrow) setProfileBarExpanded(true);
  }, [isNarrow]);

  useEffect(() => {
    setDiscussionOlderFetched([]);
    setDiscussionNoMoreOlder(false);
    discussionPageCursorRef.current = null;
    setDiscussionMentionInfoOpen(false);
  }, [activeDiscussionId]);

  useEffect(() => {
    setDmOlderFetched([]);
    setDmNoMoreOlder(false);
    dmPageCursorRef.current = null;
    setDmGroupMentionInfoOpen(false);
  }, [activeConvId]);

  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    getDoc(doc(db, 'parents', uid)).then((snap) => {
      if (cancelled || !snap.exists()) return;
      const url = snap.data().chatPhotoURL;
      if (typeof url === 'string' && url) setMyPhotoUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  useEffect(() => {
    return () => {
      if (discussionImagePreview) URL.revokeObjectURL(discussionImagePreview);
      if (dmImagePreview) URL.revokeObjectURL(dmImagePreview);
    };
  }, [discussionImagePreview, dmImagePreview]);

  useEffect(() => {
    if (!showNewDm) setDmDirectorySearch('');
  }, [showNewDm]);

  const loadDirectory = useCallback(async () => {
    if (!uid) return;
    setDirectoryLoading(true);
    try {
      const { data } = await httpsCallable(functions, 'getChatParentDirectory')();
      const list = Array.isArray(data?.parents) ? data.parents : [];
      setDirectory(list.filter((p) => p && p.uid && p.uid !== uid));
    } catch (e) {
      console.error(e);
      setDirectory([]);
    } finally {
      setDirectoryLoading(false);
    }
  }, [uid]);

  const directoryFiltered = useMemo(() => {
    const q = dmDirectorySearch.trim().toLowerCase();
    if (!q) return directory;
    return directory.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [directory, dmDirectorySearch]);

  const mentionFiltered = useMemo(() => {
    const q = mentionSearch.trim().toLowerCase();
    if (!q) return directory;
    return directory.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [directory, mentionSearch]);

  /** Other participants in the active DM / group (for @ picker). */
  const dmMentionDirectory = useMemo(() => {
    if (!activeConvId || !uid) return [];
    const conv = conversations.find((c) => c.id === activeConvId);
    if (!conv?.participants?.length) return [];
    const others = conv.participants.filter((p) => p !== uid);
    const rows = [];
    for (const id of others) {
      const fromDir = directory.find((d) => d.uid === id);
      if (fromDir) rows.push(fromDir);
      else rows.push({ uid: id, name: 'Parent', chatPhotoURL: '' });
    }
    return rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [activeConvId, conversations, uid, directory]);

  const dmMentionFiltered = useMemo(() => {
    const q = mentionSearch.trim().toLowerCase();
    if (!q) return dmMentionDirectory;
    return dmMentionDirectory.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [dmMentionDirectory, mentionSearch]);

  const handleEnablePush = async () => {
    if (!uid) return;
    setPushBusy(true);
    setPushStatus('');
    try {
      const r = await registerParentChatFcm(uid);
      if (r.ok) setPushStatus('Notifications enabled');
      else if (r.reason === 'denied') setPushStatus('Blocked in browser settings');
      else if (r.reason === 'no_vapid') setPushStatus('VAPID key not configured');
      else if (r.reason === 'unsupported') setPushStatus('Not supported on this device');
      else setPushStatus('Could not enable');
    } finally {
      setPushBusy(false);
    }
  };

  const addDiscussionMention = (p) => {
    if (!p?.uid) return;
    const name = (p.name || 'Parent').trim() || 'Parent';
    const token = `@${name} `;
    setDiscussionText((prev) => {
      const t = prev.trim();
      if (!t) return token;
      return t.endsWith(' ') ? `${t}${token}` : `${t} ${token}`;
    });
    setMentionedUserIds((prev) =>
      prev.includes(p.uid) ? prev : [...prev, p.uid].slice(0, MAX_MENTION_IDS)
    );
    setMentionOpen(false);
    setMentionSearch('');
    window.setTimeout(() => forumInputRef.current?.focus(), 0);
  };

  const addDmMention = (p) => {
    if (!p?.uid) return;
    const name = (p.name || 'Parent').trim() || 'Parent';
    const token = `@${name} `;
    setDmText((prev) => {
      const t = prev.trim();
      if (!t) return token;
      return t.endsWith(' ') ? `${t}${token}` : `${t} ${token}`;
    });
    setMentionedUserIds((prev) =>
      prev.includes(p.uid) ? prev : [...prev, p.uid].slice(0, MAX_MENTION_IDS)
    );
    setMentionOpen(false);
    setMentionSearch('');
    window.setTimeout(() => dmInputRef.current?.focus(), 0);
  };

  const removeMention = (id) => {
    setMentionedUserIds((prev) => prev.filter((x) => x !== id));
  };

  useEffect(() => {
    loadDirectory();
  }, [loadDirectory]);

  useEffect(() => {
    if (!uid) return undefined;
    const ref = doc(db, 'parents', uid, 'private', 'chatState');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setChatState(snap.exists() ? snap.data() : {});
        setChatStateLoading(false);
      },
      (err) => {
        console.error(err);
        setChatState({});
        setChatStateLoading(false);
      }
    );
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!uid) return undefined;
    const q1 = query(
      collection(db, 'discussions'),
      where('isTeamWide', '==', true),
      orderBy('lastMessageAt', 'desc')
    );
    const unsub1 = onSnapshot(
      q1,
      (snap) => {
        setDiscussionsTeamWide(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        console.error(err);
      }
    );
    return () => {
      unsub1();
    };
  }, [uid]);

  // Member-scoped threads: load via callable (Admin SDK). Client Firestore list queries for
  // isTeamWide==false + memberIds array-contains hit permission-denied with security rules
  // even when rules look correct — server-side query avoids that class of failures.
  useEffect(() => {
    if (!uid) {
      setDiscussionsMember([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await httpsCallable(functions, 'getMemberDiscussionsForParent')();
        if (cancelled) return;
        const list = Array.isArray(data?.discussions) ? data.discussions : [];
        setDiscussionsMember(list);
      } catch (e) {
        console.error(e);
        if (!cancelled) setDiscussionsMember([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const visibleDiscussions = useMemo(() => {
    const map = new Map();
    discussionsTeamWide.forEach((d) => map.set(d.id, d));
    discussionsMember.forEach((d) => map.set(d.id, d));
    return Array.from(map.values()).sort((a, b) => {
      const ta = a.lastMessageAt?.toMillis?.() || a.createdAt?.toMillis?.() || 0;
      const tb = b.lastMessageAt?.toMillis?.() || b.createdAt?.toMillis?.() || 0;
      return tb - ta;
    });
  }, [discussionsTeamWide, discussionsMember]);

  const activeDiscussion = useMemo(
    () => visibleDiscussions.find((d) => d.id === activeDiscussionId) || null,
    [visibleDiscussions, activeDiscussionId]
  );

  useEffect(() => {
    const d = searchParams.get('discussion');
    const dm = searchParams.get('dm');
    if (!d && !dm) return;
    if (d) {
      setSubTab('discussions');
      setActiveDiscussionId(d);
    }
    if (dm) {
      setSubTab('dms');
      setActiveConvId(dm);
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('discussion');
        next.delete('dm');
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    let unsub = () => {};
    let timer;
    subscribeForegroundChatMessages((payload) => {
      if (cancelled) return;
      const d = payload.data || {};
      const title = payload.notification?.title || d.title || 'New message';
      const body = payload.notification?.body || d.body || '';
      setFcmToast({ title, body });
      clearTimeout(timer);
      timer = setTimeout(() => setFcmToast(null), 6000);
    }).then((u) => {
      if (cancelled) u();
      else unsub = u;
    });
    return () => {
      cancelled = true;
      clearTimeout(timer);
      unsub();
    };
  }, [uid]);

  useEffect(() => {
    if (!mentionOpen) return undefined;
    const close = (e) => {
      if (mentionPanelRef.current && !mentionPanelRef.current.contains(e.target)) {
        setMentionOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [mentionOpen]);

  useEffect(() => {
    if (visibleDiscussions.length === 1) {
      setActiveDiscussionId(visibleDiscussions[0].id);
    } else if (visibleDiscussions.length === 0) {
      setActiveDiscussionId(null);
    }
  }, [visibleDiscussions]);

  useEffect(() => {
    if (!uid || !activeDiscussionId) {
      setDiscussionLiveWindow([]);
      setDiscussionLoading(false);
      return undefined;
    }
    const col = collection(db, 'discussions', activeDiscussionId, 'messages');
    const q = query(col, orderBy('createdAt', 'desc'), limit(FORUM_PAGE));
    setDiscussionLoading(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const asc = snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
        setDiscussionLiveWindow(asc);
        discussionLiveOldestSnapRef.current = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
        setDiscussionLoading(false);
      },
      (err) => {
        console.error(err);
        setDiscussionLoading(false);
      }
    );
    return () => unsub();
  }, [uid, activeDiscussionId]);

  useEffect(() => {
    if (!uid || subTab !== 'discussions' || !activeDiscussionId) return undefined;
    const r = doc(db, 'parents', uid, 'private', 'chatState');
    const t = setTimeout(() => {
      getDoc(r)
        .then((snap) => {
          const prev =
            snap.exists() && snap.data().discussionReads && typeof snap.data().discussionReads === 'object'
              ? snap.data().discussionReads
              : {};
          return setDoc(
            r,
            { discussionReads: { ...prev, [activeDiscussionId]: serverTimestamp() } },
            { merge: true }
          );
        })
        .catch((e) => console.error(e));
    }, 400);
    return () => clearTimeout(t);
  }, [uid, subTab, activeDiscussionId, discussionMessages.length]);

  const discussionReads = useMemo(() => {
    const m = chatState?.discussionReads;
    if (m && typeof m === 'object') return m;
    return {};
  }, [chatState]);
  const dmReads = useMemo(() => {
    const m = chatState?.dmReads;
    if (m && typeof m === 'object') return m;
    return EMPTY_DM_READS;
  }, [chatState]);

  const archivedDmIdSet = useMemo(() => {
    const raw = chatState?.archivedConversationIds;
    if (!Array.isArray(raw)) return new Set();
    return new Set(raw.filter((x) => typeof x === 'string' && x.length > 0));
  }, [chatState]);

  const { activeDmConversations, archivedDmConversations } = useMemo(() => {
    const active = [];
    const archived = [];
    for (const c of conversations) {
      if (!c?.id) continue;
      if (archivedDmIdSet.has(c.id)) archived.push(c);
      else active.push(c);
    }
    const sortFn = (a, b) => {
      const ta = a.lastMessageAt?.toMillis?.() ?? 0;
      const tb = b.lastMessageAt?.toMillis?.() ?? 0;
      return tb - ta;
    };
    active.sort(sortFn);
    archived.sort(sortFn);
    return { activeDmConversations: active, archivedDmConversations: archived };
  }, [conversations, archivedDmIdSet]);

  const discussionsUnread = useMemo(() => {
    return visibleDiscussions.some((d) => {
      const lm = d.lastMessageAt;
      if (!lm || !lm.toMillis) return false;
      const read = discussionReads[d.id];
      const readMs = read && read.toMillis ? read.toMillis() : 0;
      return lm.toMillis() > readMs;
    });
  }, [visibleDiscussions, discussionReads]);

  // Messages tab: load conversations via callable (Admin SDK). Client list queries on conversations
  // were still hitting permission-denied with security rules for some accounts.
  useEffect(() => {
    if (!uid || subTab !== 'dms') {
      setConvLoading(false);
      if (subTab !== 'dms') setConversations([]);
      return undefined;
    }
    let cancelled = false;
    setConvLoading(true);
    (async () => {
      try {
        const { data } = await httpsCallable(functions, 'getConversationsForParent')();
        if (cancelled) return;
        const rows = Array.isArray(data?.conversations) ? data.conversations : [];
        rows.sort((a, b) => {
          const ta = a.lastMessageAt?.toMillis?.() ?? 0;
          const tb = b.lastMessageAt?.toMillis?.() ?? 0;
          return tb - ta;
        });
        setConversations(rows);
      } catch (e) {
        console.error(e);
        if (!cancelled) setConversations([]);
      } finally {
        if (!cancelled) setConvLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid, subTab]);

  // Only subscribe to DM thread messages while on Messages tab (avoids listeners firing on Discussions).
  useEffect(() => {
    if (!activeConvId || !uid || subTab !== 'dms') {
      setDmLiveWindow([]);
      return undefined;
    }
    const col = collection(db, 'conversations', activeConvId, 'messages');
    const q = query(col, orderBy('createdAt', 'desc'), limit(DM_PAGE));
    setDmLoading(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const asc = snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
        setDmLiveWindow(asc);
        dmLiveOldestSnapRef.current = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
        setDmLoading(false);
      },
      (err) => {
        console.error(err);
        setDmLoading(false);
      }
    );
    return () => unsub();
  }, [activeConvId, uid, subTab]);

  useEffect(() => {
    if (!uid || !activeConvId || subTab !== 'dms') return undefined;
    const t = setTimeout(() => {
      const r = doc(db, 'parents', uid, 'private', 'chatState');
      getDoc(r)
        .then((snap) => {
          const prev =
            snap.exists() && snap.data().dmReads && typeof snap.data().dmReads === 'object'
              ? snap.data().dmReads
              : {};
          return setDoc(r, { dmReads: { ...prev, [activeConvId]: Timestamp.now() } }, { merge: true });
        })
        .catch((e) => console.error(e));
    }, 400);
    return () => clearTimeout(t);
  }, [uid, activeConvId, subTab, dmMessages.length]);

  const dmUnread = useMemo(() => {
    return conversations.some((c) => {
      if (archivedDmIdSet.has(c.id)) return false;
      const lm = c.lastMessageAt;
      if (!lm || !lm.toMillis) return false;
      const read = dmReads[c.id];
      const readMs = read && read.toMillis ? read.toMillis() : 0;
      return lm.toMillis() > readMs;
    });
  }, [conversations, dmReads, archivedDmIdSet]);

  useEffect(() => {
    if (activeConvId && archivedDmIdSet.has(activeConvId)) {
      setDmArchivedSectionOpen(true);
    }
  }, [activeConvId, archivedDmIdSet]);

  const onForumScroll = useCallback(() => {
    const el = forumStreamRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    forumNearBottomRef.current = dist < SCROLL_NEAR_BOTTOM_PX;
    if (forumNearBottomRef.current) setShowForumJump(false);
  }, []);

  const onDmScroll = useCallback(() => {
    const el = dmStreamRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    dmNearBottomRef.current = dist < SCROLL_NEAR_BOTTOM_PX;
    if (dmNearBottomRef.current) setShowDmJump(false);
  }, []);

  const scrollForumToBottom = useCallback((behavior) => {
    forumEndRef.current?.scrollIntoView({ behavior: behavior || 'auto' });
    forumNearBottomRef.current = true;
    setShowForumJump(false);
  }, []);

  const scrollDmToBottom = useCallback((behavior) => {
    dmEndRef.current?.scrollIntoView({ behavior: behavior || 'auto' });
    dmNearBottomRef.current = true;
    setShowDmJump(false);
  }, []);

  const loadDiscussionOlder = useCallback(async () => {
    if (!uid || !activeDiscussionId || discussionLoadingOlder || discussionNoMoreOlder) return;
    const cursor = discussionPageCursorRef.current || discussionLiveOldestSnapRef.current;
    if (!cursor) return;
    const col = collection(db, 'discussions', activeDiscussionId, 'messages');
    const el = forumStreamRef.current;
    const prevH = el?.scrollHeight ?? 0;
    const prevTop = el?.scrollTop ?? 0;
    setDiscussionLoadingOlder(true);
    try {
      const qy = query(col, orderBy('createdAt', 'desc'), startAfter(cursor), limit(FORUM_PAGE));
      const snap = await getDocs(qy);
      if (snap.empty) {
        setDiscussionNoMoreOlder(true);
        return;
      }
      const batch = snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
      discussionPageCursorRef.current = snap.docs[snap.docs.length - 1];
      setDiscussionOlderFetched((prev) => mergeMessagesByIdAsc(batch, prev));
      if (snap.docs.length < FORUM_PAGE) setDiscussionNoMoreOlder(true);
      requestAnimationFrame(() => {
        const node = forumStreamRef.current;
        if (!node) return;
        node.scrollTop = prevTop + (node.scrollHeight - prevH);
      });
    } catch (e) {
      console.error(e);
      showToast('Could not load older messages.');
    } finally {
      setDiscussionLoadingOlder(false);
    }
  }, [uid, activeDiscussionId, discussionLoadingOlder, discussionNoMoreOlder, showToast]);

  const loadDmOlder = useCallback(async () => {
    if (!uid || !activeConvId || dmLoadingOlder || dmNoMoreOlder) return;
    const cursor = dmPageCursorRef.current || dmLiveOldestSnapRef.current;
    if (!cursor) return;
    const col = collection(db, 'conversations', activeConvId, 'messages');
    const el = dmStreamRef.current;
    const prevH = el?.scrollHeight ?? 0;
    const prevTop = el?.scrollTop ?? 0;
    setDmLoadingOlder(true);
    try {
      const qy = query(col, orderBy('createdAt', 'desc'), startAfter(cursor), limit(DM_PAGE));
      const snap = await getDocs(qy);
      if (snap.empty) {
        setDmNoMoreOlder(true);
        return;
      }
      const batch = snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
      dmPageCursorRef.current = snap.docs[snap.docs.length - 1];
      setDmOlderFetched((prev) => mergeMessagesByIdAsc(batch, prev));
      if (snap.docs.length < DM_PAGE) setDmNoMoreOlder(true);
      requestAnimationFrame(() => {
        const node = dmStreamRef.current;
        if (!node) return;
        node.scrollTop = prevTop + (node.scrollHeight - prevH);
      });
    } catch (e) {
      console.error(e);
      showToast('Could not load older messages.');
    } finally {
      setDmLoadingOlder(false);
    }
  }, [uid, activeConvId, dmLoadingOlder, dmNoMoreOlder, showToast]);

  useEffect(() => {
    forumForceScrollRef.current = true;
  }, [activeDiscussionId]);

  useEffect(() => {
    if (subTab === 'discussions') forumForceScrollRef.current = true;
  }, [subTab]);

  useEffect(() => {
    if (subTab !== 'discussions' || discussionLoading || chatStateLoading) return;
    const tail = discussionMessages.length ? discussionMessages[discussionMessages.length - 1] : null;
    const prevTailId = forumTailIdRef.current;
    if (tail) forumTailIdRef.current = tail.id;
    else forumTailIdRef.current = null;
    if (!tail) {
      if (forumForceScrollRef.current) forumForceScrollRef.current = false;
      return;
    }
    if (forumForceScrollRef.current) {
      forumForceScrollRef.current = false;
      requestAnimationFrame(() => scrollForumToBottom('auto'));
      return;
    }
    if (tail.id !== prevTailId && prevTailId && !forumNearBottomRef.current) {
      setShowForumJump(true);
      return;
    }
    if (forumNearBottomRef.current) {
      requestAnimationFrame(() => scrollForumToBottom('smooth'));
    }
  }, [subTab, discussionMessages, discussionLoading, chatStateLoading, scrollForumToBottom]);

  useEffect(() => {
    dmForceScrollRef.current = true;
  }, [activeConvId]);

  useEffect(() => {
    if (subTab === 'dms') dmForceScrollRef.current = true;
  }, [subTab]);

  useEffect(() => {
    if (subTab !== 'dms' || dmLoading) return;
    const tail = dmMessages.length ? dmMessages[dmMessages.length - 1] : null;
    const prevTailId = dmTailIdRef.current;
    if (tail) dmTailIdRef.current = tail.id;
    else dmTailIdRef.current = null;
    if (!tail) {
      if (dmForceScrollRef.current) dmForceScrollRef.current = false;
      return;
    }
    if (dmForceScrollRef.current) {
      dmForceScrollRef.current = false;
      requestAnimationFrame(() => scrollDmToBottom('auto'));
      return;
    }
    if (tail.id !== prevTailId && prevTailId && !dmNearBottomRef.current) {
      setShowDmJump(true);
      return;
    }
    if (dmNearBottomRef.current) {
      requestAnimationFrame(() => scrollDmToBottom('smooth'));
    }
  }, [subTab, dmMessages, dmLoading, scrollDmToBottom]);

  const uploadChatImage = async (file) => {
    if (!uid || !file) return null;
    const rawExt = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const ext = /^[a-z0-9]+$/.test(rawExt) ? rawExt.slice(0, 5) : 'jpg';
    const path = `parents/${uid}/chatUploads/${Date.now()}_${Math.random().toString(36).slice(2, 9)}.${ext}`;
    const sref = ref(storage, path);
    await uploadBytes(sref, file, { contentType: file.type || 'image/jpeg' });
    return getDownloadURL(sref);
  };

  const handleAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uid) return;
    if (!file.type.startsWith('image/')) {
      showToast('Please choose an image file.');
      return;
    }
    if (file.size > MAX_CHAT_IMAGE_BYTES) {
      showToast('Photo must be 5 MB or smaller.');
      return;
    }
    setAvatarUploading(true);
    try {
      const rawExt = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const ext = /^[a-z0-9]+$/.test(rawExt) ? rawExt.slice(0, 5) : 'jpg';
      const path = `parents/${uid}/chatAvatar/profile.${ext}`;
      const sref = ref(storage, path);
      await uploadBytes(sref, file, { contentType: file.type || 'image/jpeg' });
      const url = await getDownloadURL(sref);
      await updateDoc(doc(db, 'parents', uid), { chatPhotoURL: url });
      setMyPhotoUrl(url);
      if (typeof onProfilePhotoUpdated === 'function') onProfilePhotoUpdated(url);
    } catch (err) {
      console.error(err);
      showToast('Could not update profile photo. Try again.');
    } finally {
      setAvatarUploading(false);
      e.target.value = '';
    }
  };

  const onDiscussionImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Choose an image.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_CHAT_IMAGE_BYTES) {
      showToast('Image must be 5 MB or smaller.');
      e.target.value = '';
      return;
    }
    if (discussionImagePreview) URL.revokeObjectURL(discussionImagePreview);
    setDiscussionImageFile(file);
    setDiscussionImagePreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const clearDiscussionImage = () => {
    if (discussionImagePreview) URL.revokeObjectURL(discussionImagePreview);
    setDiscussionImageFile(null);
    setDiscussionImagePreview(null);
  };

  const onDmImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Choose an image.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_CHAT_IMAGE_BYTES) {
      showToast('Image must be 5 MB or smaller.');
      e.target.value = '';
      return;
    }
    if (dmImagePreview) URL.revokeObjectURL(dmImagePreview);
    setDmImageFile(file);
    setDmImagePreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const clearDmImage = () => {
    if (dmImagePreview) URL.revokeObjectURL(dmImagePreview);
    setDmImageFile(null);
    setDmImagePreview(null);
  };

  const sendDiscussion = async (e) => {
    e.preventDefault();
    const text = discussionText.trim();
    if ((!text && !discussionImageFile) || !uid || !activeDiscussionId || discussionSending) return;
    setDiscussionSending(true);
    try {
      let imageUrl = null;
      if (discussionImageFile) {
        imageUrl = await uploadChatImage(discussionImageFile);
      }
      const payload = {
        text: text || '',
        authorId: uid,
        authorName: displayName,
        createdAt: serverTimestamp(),
        ...(myPhotoUrl ? { authorPhotoUrl: myPhotoUrl } : {}),
        ...(imageUrl ? { imageUrl } : {}),
      };
      if (replyToDiscussion?.id) payload.replyToMessageId = replyToDiscussion.id;
      if (mentionedUserIds.length > 0) {
        payload.mentionedUserIds = [...new Set(mentionedUserIds)].slice(0, MAX_MENTION_IDS);
      }
      const dref = doc(db, 'discussions', activeDiscussionId);
      const msgRef = doc(collection(db, 'discussions', activeDiscussionId, 'messages'));
      const batch = writeBatch(db);
      batch.set(msgRef, payload);
      batch.update(dref, {
        lastMessageAt: serverTimestamp(),
        lastMessagePreview: previewForList(text, imageUrl),
      });
      await batch.commit();
      setDiscussionText('');
      clearDiscussionImage();
      setReplyToDiscussion(null);
      setMentionedUserIds([]);
    } catch (err) {
      console.error(err);
      showToast('Could not send message. Check your connection and try again.');
    } finally {
      setDiscussionSending(false);
    }
  };

  const removeDiscussionMessage = async (msg) => {
    if (!msg?.id || msg.authorId !== uid || !activeDiscussionId) return;
    try {
      await deleteDoc(doc(db, 'discussions', activeDiscussionId, 'messages', msg.id));
    } catch (err) {
      console.error(err);
      showToast('Could not delete message.');
    }
  };

  const openOrCreateDm = async () => {
    if (!uid) return;
    if (newDmPickIds.length === 1) {
      const other = newDmPickIds[0];
      const cid = canonicalConversationId(uid, other);
      const cref = doc(db, 'conversations', cid);
      try {
        const snap = await getDoc(cref);
        if (!snap.exists()) {
          const otherParentSnap = await getDoc(doc(db, 'parents', other));
          if (!otherParentSnap.exists()) {
            showToast(
              'That parent is not in the directory yet. Ask an admin to add them or have them sign up before starting a DM.'
            );
            return;
          }
          const [a, b] = uid < other ? [uid, other] : [other, uid];
          // Omit lastMessageAuthorId when empty — Firestore rules allow 4-key or 5-key creates.
          await setDoc(cref, {
            participants: [a, b],
            createdAt: serverTimestamp(),
            lastMessageAt: serverTimestamp(),
            lastMessagePreview: '',
          });
        }
        setActiveConvId(cid);
        setShowNewDm(false);
        setNewDmPickIds([]);
      } catch (err) {
        console.error(err);
        showToast(
          err?.code === 'permission-denied'
            ? 'Could not start the chat (permissions). Ask an admin to confirm both accounts are active parent profiles.'
            : err?.message || 'Could not open chat.'
        );
      }
      return;
    }
    if (newDmPickIds.length >= 2) {
      try {
        const { data } = await httpsCallable(functions, 'createGroupConversation')({
          participantIds: newDmPickIds,
        });
        const cid = data?.conversationId;
        if (cid) {
          setActiveConvId(cid);
          setShowNewDm(false);
          setNewDmPickIds([]);
        }
      } catch (err) {
        console.error(err);
        showToast(err.message || 'Could not create group chat.');
      }
    }
  };

  const sendDm = async (e) => {
    e.preventDefault();
    const text = dmText.trim();
    if ((!text && !dmImageFile) || !uid || !activeConvId || dmSending) return;
    let conv = conversations.find((c) => c.id === activeConvId);
    if (!conv?.participants?.includes(uid)) {
      const snap = await getDoc(doc(db, 'conversations', activeConvId));
      if (!snap.exists() || !(snap.data().participants || []).includes(uid)) return;
      conv = { id: activeConvId, ...snap.data() };
    }
    const otherParticipant = conv.participants.find((p) => p !== uid);
    if (!otherParticipant) return;
    setDmSending(true);
    try {
      let imageUrl = null;
      if (dmImageFile) {
        imageUrl = await uploadChatImage(dmImageFile);
      }
      const cref = doc(db, 'conversations', activeConvId);
      const csnap = await getDoc(cref);
      const batch = writeBatch(db);
      const msgRef = doc(collection(db, 'conversations', activeConvId, 'messages'));
      const dmPayload = {
        text: text || '',
        authorId: uid,
        authorName: displayName,
        createdAt: serverTimestamp(),
        ...(myPhotoUrl ? { authorPhotoUrl: myPhotoUrl } : {}),
        ...(imageUrl ? { imageUrl } : {}),
        ...(replyToDm?.id ? { replyToMessageId: replyToDm.id } : {}),
      };
      if (mentionedUserIds.length > 0) {
        dmPayload.mentionedUserIds = [...new Set(mentionedUserIds)].slice(0, MAX_MENTION_IDS);
      }
      batch.set(msgRef, dmPayload);
      const pv = previewForList(text, imageUrl);
      if (!csnap.exists()) {
        const [a, b] = uid < otherParticipant ? [uid, otherParticipant] : [otherParticipant, uid];
        batch.set(cref, {
          participants: [a, b],
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
          lastMessagePreview: pv,
          lastMessageAuthorId: uid,
        });
      } else {
        batch.set(
          cref,
          {
            lastMessageAt: serverTimestamp(),
            lastMessagePreview: pv,
            lastMessageAuthorId: uid,
          },
          { merge: true }
        );
      }
      await batch.commit();
      setDmText('');
      clearDmImage();
      setReplyToDm(null);
      setMentionedUserIds([]);
      setMentionOpen(false);
      setMentionSearch('');
    } catch (err) {
      console.error(err);
      showToast('Could not send direct message.');
    } finally {
      setDmSending(false);
    }
  };

  const removeDmMessage = async (msg) => {
    if (!msg?.id || msg.authorId !== uid || !activeConvId) return;
    try {
      await deleteDoc(doc(db, 'conversations', activeConvId, 'messages', msg.id));
    } catch (err) {
      console.error(err);
      showToast('Could not delete message.');
    }
  };

  const archiveDmConversation = async (convId) => {
    if (!uid || !convId) return;
    try {
      const r = doc(db, 'parents', uid, 'private', 'chatState');
      await setDoc(r, { archivedConversationIds: arrayUnion(convId) }, { merge: true });
      if (activeConvId === convId) setActiveConvId(null);
    } catch (e) {
      console.error(e);
      showToast('Could not archive conversation.');
    }
  };

  const unarchiveDmConversation = async (convId) => {
    if (!uid || !convId) return;
    try {
      const r = doc(db, 'parents', uid, 'private', 'chatState');
      await setDoc(r, { archivedConversationIds: arrayRemove(convId) }, { merge: true });
    } catch (e) {
      console.error(e);
      showToast('Could not restore conversation.');
    }
  };

  const activeConv = conversations.find((c) => c.id === activeConvId);
  const isGroupChat =
    activeConv &&
    activeConv.participants &&
    (activeConv.participants.length > 2 || activeConv.isGroup === true);
  const otherUids =
    activeConv && activeConv.participants
      ? activeConv.participants.filter((p) => p !== uid)
      : [];
  const otherUid = otherUids.length === 1 ? otherUids[0] : null;
  const otherName = otherUid ? directory.find((d) => d.uid === otherUid)?.name || 'Parent' : '';
  const otherPhoto = otherUid ? directory.find((d) => d.uid === otherUid)?.chatPhotoURL : '';
  const groupTitle =
    isGroupChat && otherUids.length
      ? otherUids
          .map((id) => directory.find((d) => d.uid === id)?.name || 'Parent')
          .join(', ')
      : '';

  const handleMobileSheetReply = () => {
    if (!mobileMsgMenu) return;
    const { type, msg } = mobileMsgMenu;
    setMobileMsgMenu(null);
    if (type === 'discussion') {
      const official = msg.authorId === 'admin' || msg.isOfficial;
      setReplyToDiscussion({
        id: msg.id,
        authorName: msg.authorName || (official ? 'Team' : 'Parent'),
      });
      setDiscussionActionId(null);
      window.setTimeout(() => forumInputRef.current?.focus(), 0);
    } else {
      setReplyToDm({ id: msg.id, authorName: msg.authorName || 'Parent' });
      setDmActionId(null);
      window.setTimeout(() => dmInputRef.current?.focus(), 0);
    }
  };

  const handleMobileSheetDelete = () => {
    if (!mobileMsgMenu) return;
    const { type, msg } = mobileMsgMenu;
    setMobileMsgMenu(null);
    setPendingDelete({ channel: type === 'discussion' ? 'discussion' : 'dm', msg });
  };

  useEffect(() => {
    setReplyToDiscussion(null);
    setReplyToDm(null);
    setDiscussionActionId(null);
    setDmActionId(null);
    setMobileMsgMenu(null);
    setMentionedUserIds([]);
    setMentionOpen(false);
    setMentionSearch('');
  }, [subTab]);

  useEffect(() => {
    setReplyToDm(null);
    setDmActionId(null);
    setMobileMsgMenu(null);
    setMentionedUserIds([]);
    setMentionOpen(false);
    setMentionSearch('');
  }, [activeConvId]);

  useEffect(() => {
    if (!mobileMsgMenu) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileMsgMenu(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileMsgMenu]);

  useEffect(() => {
    if (!pendingDelete) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setPendingDelete(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pendingDelete]);

  useEffect(() => {
    if (discussionActionId == null && dmActionId == null) return undefined;
    const close = (e) => {
      if (e.target.closest('.parent-chat-msg-interactive')) return;
      setDiscussionActionId(null);
      setDmActionId(null);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [discussionActionId, dmActionId]);

  const renderReplyContext = (m, list) => {
    if (!m.replyToMessageId) return null;
    const parentMsg = list.find((x) => x.id === m.replyToMessageId);
    return (
      <div className="parent-chat-reply-quote">
        {parentMsg?.imageUrl ? (
          <img src={parentMsg.imageUrl} alt="" className="parent-chat-reply-thumb" />
        ) : null}
        <span className="parent-chat-reply-text">{replyLinePreview(parentMsg)}</span>
      </div>
    );
  };

  const renderDmSidebarRow = (c, isArchivedRow) => {
    const others = (c.participants || []).filter((p) => p !== uid);
    const isGroup = others.length > 1 || c.isGroup === true;
    const firstOther = others[0];
    const d = firstOther ? directory.find((x) => x.uid === firstOther) : null;
    const label = isGroup ? `Group (${c.participants?.length || 0} people)` : d?.name || 'Parent';
    const photo = !isGroup && d?.chatPhotoURL ? d.chatPhotoURL : null;
    const unread =
      c.lastMessageAt?.toMillis &&
      (!dmReads[c.id] || c.lastMessageAt.toMillis() > dmReads[c.id].toMillis());
    return (
      <li key={c.id} className="parent-chat-conv-li-wrap">
        <button
          type="button"
          className={`parent-chat-conv-item ${activeConvId === c.id ? 'active' : ''}`}
          onClick={() => setActiveConvId(c.id)}
        >
          <span className="parent-chat-conv-avatar">
            {photo ? (
              <img src={photo} alt="" />
            ) : (
              <span className="parent-chat-avatar-placeholder sm">{initialsFromName(label)}</span>
            )}
          </span>
          <span className="parent-chat-conv-text">
            <span className="parent-chat-conv-name">
              {label}
              {unread ? <span className="parent-chat-badge" aria-label="Unread" /> : null}
            </span>
            <span className="parent-chat-conv-preview">{c.lastMessagePreview || '—'}</span>
          </span>
        </button>
        <button
          type="button"
          className="parent-chat-conv-archive-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (isArchivedRow) void unarchiveDmConversation(c.id);
            else void archiveDmConversation(c.id);
          }}
          aria-label={isArchivedRow ? `Restore ${label}` : `Archive ${label}`}
        >
          {isArchivedRow ? 'Restore' : 'Archive'}
        </button>
      </li>
    );
  };

  const rootClass = `parent-chat parent-chat--${layout}${layout === 'page' ? ' parent-chat--page' : ''}`;

  /** List vs thread: show discussion list full width until user opens a thread; then hide list and show thread full width (all breakpoints). */
  const showDiscussionSidebar = visibleDiscussions.length > 1 && !activeDiscussionId;
  const showDiscussionThread =
    visibleDiscussions.length === 0 ||
    visibleDiscussions.length === 1 ||
    (visibleDiscussions.length > 1 && activeDiscussionId);
  const discussionLayoutThreadOnly = !(showDiscussionSidebar && showDiscussionThread);

  /** Same pattern as discussions: conversation list OR active thread, not side-by-side on desktop. */
  const showDmList = !activeConvId;
  const showDmThread = !!activeConvId;
  const dmLayoutThreadOnly = !(showDmList && showDmThread);

  const renderBubbleContent = (m, mine, official) => {
    const hasImg = !!(m.imageUrl && String(m.imageUrl).trim());
    const hasText = !!(m.text && String(m.text).trim());
    return (
      <div className={`parent-chat-bubble-inner ${mine ? 'is-mine' : ''} ${official ? 'is-official' : ''}`}>
        {hasImg && (
          <a href={m.imageUrl} target="_blank" rel="noopener noreferrer" className="parent-chat-img-wrap">
            <img src={m.imageUrl} alt="" className="parent-chat-msg-img" />
          </a>
        )}
        {hasText ? <p className="parent-chat-body">{m.text}</p> : null}
      </div>
    );
  };

  return (
    <section className={rootClass} aria-label="Team chat">
      {fcmToast ? (
        <div className="parent-chat-fcm-toast" role="status">
          <div className="parent-chat-fcm-toast-text">
            <div className="parent-chat-fcm-toast-title">{fcmToast.title}</div>
            {fcmToast.body ? <div className="parent-chat-fcm-toast-body">{fcmToast.body}</div> : null}
          </div>
          <button type="button" className="parent-chat-fcm-toast-x" onClick={() => setFcmToast(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      ) : null}
      {appToast ? (
        <div className="parent-chat-app-toast" role="status">
          {appToast.message}
        </div>
      ) : null}
      <div
        className={`parent-chat-profile-bar ${isNarrow ? 'parent-chat-profile-bar--narrow' : ''} ${
          isNarrow && !profileBarExpanded ? 'parent-chat-profile-bar--collapsed' : ''
        }`}
      >
        {isNarrow && !profileBarExpanded ? (
          <button
            type="button"
            className="parent-chat-profile-compact"
            onClick={() => setProfileBarExpanded(true)}
            aria-expanded="false"
          >
            <span className="parent-chat-profile-compact-avatar">
              {myPhotoUrl ? (
                <img src={myPhotoUrl} alt="" className="parent-chat-profile-img" />
              ) : (
                <span className="parent-chat-profile-placeholder sm">{initialsFromName(displayName)}</span>
              )}
            </span>
            <span className="parent-chat-profile-compact-text">
              <span className="parent-chat-profile-compact-title">Account &amp; notifications</span>
              <span className="parent-chat-profile-compact-sub">Photo, push alerts</span>
            </span>
            <span className="parent-chat-profile-compact-chev" aria-hidden>
              ▼
            </span>
          </button>
        ) : (
          <>
            <div className="parent-chat-profile-avatar-wrap">
              {myPhotoUrl ? (
                <img src={myPhotoUrl} alt="" className="parent-chat-profile-img" />
              ) : (
                <div className="parent-chat-profile-placeholder">{initialsFromName(displayName)}</div>
              )}
              <label className="parent-chat-profile-upload">
                {avatarUploading ? '…' : 'Photo'}
                <input type="file" accept="image/*" className="visually-hidden" onChange={handleAvatarPick} disabled={avatarUploading} />
              </label>
            </div>
            <div className="parent-chat-profile-meta">
              {!myPhotoUrl ? (
                <p className="parent-chat-profile-hint">Add a photo so others recognize you.</p>
              ) : null}
              <div className="parent-chat-push-row">
                <button
                  type="button"
                  className="parent-chat-btn-secondary parent-chat-push-btn"
                  onClick={handleEnablePush}
                  disabled={pushBusy}
                >
                  {pushBusy ? '…' : typeof Notification !== 'undefined' && Notification.permission === 'granted'
                    ? 'Refresh notifications'
                    : 'Enable notifications'}
                </button>
                {pushStatus ? <span className="parent-chat-push-status">{pushStatus}</span> : null}
              </div>
            </div>
            {isNarrow ? (
              <button type="button" className="parent-chat-profile-done" onClick={() => setProfileBarExpanded(false)}>
                Done
              </button>
            ) : null}
          </>
        )}
      </div>

      <div className="parent-chat-segmented" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'discussions'}
          className={`parent-chat-seg ${subTab === 'discussions' ? 'active' : ''}`}
          onClick={() => setSubTab('discussions')}
        >
          Discussions
          {discussionsUnread ? <span className="parent-chat-badge" aria-label="Unread" /> : null}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={subTab === 'dms'}
          className={`parent-chat-seg ${subTab === 'dms' ? 'active' : ''}`}
          onClick={() => setSubTab('dms')}
        >
          Messages
          {dmUnread ? <span className="parent-chat-badge" aria-label="Unread" /> : null}
        </button>
      </div>

      {subTab === 'discussions' && (
        <div
          className={`parent-chat-dm-layout ${
            discussionLayoutThreadOnly ? 'parent-chat-dm-layout--thread-only' : ''
          }`}
        >
          {showDiscussionSidebar && (
            <div className="parent-chat-dm-sidebar">
              <p className="parent-chat-discussion-sidebar-title">Discussions</p>
              <ul className="parent-chat-conv-list">
                {visibleDiscussions.map((d) => {
                  const unread =
                    d.lastMessageAt?.toMillis &&
                    (!discussionReads[d.id] ||
                      d.lastMessageAt.toMillis() > discussionReads[d.id].toMillis());
                  return (
                    <li key={d.id}>
                      <button
                        type="button"
                        className={`parent-chat-conv-item ${activeDiscussionId === d.id ? 'active' : ''}`}
                        onClick={() => setActiveDiscussionId(d.id)}
                      >
                        <span className="parent-chat-conv-text">
                          <span className="parent-chat-conv-name">
                            {d.title || 'Discussion'}
                            {unread ? <span className="parent-chat-badge" /> : null}
                          </span>
                          <span className="parent-chat-conv-preview">{d.lastMessagePreview || '—'}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {showDiscussionThread && (
            <div className="parent-chat-dm-thread parent-chat-panel parent-chat-panel--forum">
              {visibleDiscussions.length === 0 ? (
                <p className="parent-chat-muted parent-chat-dm-empty">
                  No discussions yet. Check back soon.
                </p>
              ) : null}
              {(visibleDiscussions.length === 1 || activeDiscussionId) && (
                <>
                  <div className="parent-chat-dm-thread-header">
                    {visibleDiscussions.length > 1 && activeDiscussionId ? (
                      <button
                        type="button"
                        className="parent-chat-back"
                        onClick={() => setActiveDiscussionId(null)}
                        aria-label="Back to discussions list"
                      >
                        ←
                      </button>
                    ) : null}
                    <h2 className="parent-chat-dm-title">
                      {visibleDiscussions.length === 1
                        ? visibleDiscussions[0].title || 'Discussion'
                        : visibleDiscussions.find((x) => x.id === activeDiscussionId)?.title || 'Discussion'}
                    </h2>
                  </div>
                  <div className="parent-chat-stream-wrap">
                    {showForumJump ? (
                      <button
                        type="button"
                        className="parent-chat-jump-latest"
                        onClick={() => scrollForumToBottom('smooth')}
                      >
                        Jump to latest
                      </button>
                    ) : null}
                    <div
                      ref={forumStreamRef}
                      className="parent-chat-stream"
                      role="log"
                      aria-live="polite"
                      onScroll={onForumScroll}
                    >
                      {discussionLoading || chatStateLoading ? (
                        <p className="parent-chat-muted parent-chat-stream-loading">Loading…</p>
                      ) : (
                        <>
                          {!discussionNoMoreOlder &&
                          (discussionLiveWindow.length >= FORUM_PAGE || discussionOlderFetched.length > 0) ? (
                            <div className="parent-chat-load-older-wrap">
                              <button
                                type="button"
                                className="parent-chat-load-older"
                                onClick={() => void loadDiscussionOlder()}
                                disabled={discussionLoadingOlder}
                              >
                                {discussionLoadingOlder ? 'Loading…' : 'Load older messages'}
                              </button>
                            </div>
                          ) : null}
                          {discussionMessages.map((m, i) => {
                            const official = m.authorId === 'admin' || m.isOfficial;
                            const prevMsg = i > 0 ? discussionMessages[i - 1] : null;
                            const nextMsg = i + 1 < discussionMessages.length ? discussionMessages[i + 1] : null;
                            const d = messageDate(m);
                            const prevD = prevMsg ? messageDate(prevMsg) : null;
                            const daySep =
                              d &&
                              (!prevD || !sameCalendarDay(prevD, d)) ? (
                                <div key={`day-${m.id}`} className="parent-chat-day-sep" role="separator">
                                  {dateSeparatorLabel(d)}
                                </div>
                              ) : null;
                            const gPrev = !!(prevMsg && messagesGroupTogether(prevMsg, m) && !official);
                            const gNext = !!(nextMsg && messagesGroupTogether(m, nextMsg) && !official);
                            return (
                              <React.Fragment key={m.id}>
                                {daySep}
                                <ForumMessageRow
                                  m={m}
                                  uid={uid}
                                  displayName={displayName}
                                  myPhotoUrl={myPhotoUrl}
                                  threadMessages={discussionMessages}
                                  isNarrow={isNarrow}
                                  actionOpen={discussionActionId === m.id}
                                  onDesktopActivate={() =>
                                    setDiscussionActionId((prev) => (prev === m.id ? null : m.id))
                                  }
                                  onReply={() => {
                                    setReplyToDiscussion({
                                      id: m.id,
                                      authorName: m.authorName || (official ? 'Team' : 'Parent'),
                                    });
                                    setDiscussionActionId(null);
                                  }}
                                  onRequestDelete={() => setPendingDelete({ channel: 'discussion', msg: m })}
                                  onMobileMenuOpen={
                                    isNarrow
                                      ? () => setMobileMsgMenu({ type: 'discussion', msg: m })
                                      : undefined
                                  }
                                  renderBubbleContent={renderBubbleContent}
                                  renderReplyContext={renderReplyContext}
                                  timeShort={msgTimeShort(m)}
                                  groupWithPrev={gPrev}
                                  groupWithNext={gNext}
                                />
                              </React.Fragment>
                            );
                          })}
                          <div ref={forumEndRef} />
                        </>
                      )}
                    </div>
                  </div>

                  <form className="parent-chat-composer" onSubmit={sendDiscussion}>
                    {discussionImagePreview && (
                      <div className="parent-chat-pending-img">
                        <img src={discussionImagePreview} alt="" />
                        <button
                          type="button"
                          className="parent-chat-pending-remove"
                          onClick={clearDiscussionImage}
                          aria-label="Remove image"
                        >
                          ×
                        </button>
                      </div>
                    )}
                    {replyToDiscussion && (
                      <ReplyComposerPreview
                        authorLabel={replyToDiscussion.authorName}
                        quotedMessage={discussionMessages.find((x) => x.id === replyToDiscussion.id)}
                        onDismiss={() => setReplyToDiscussion(null)}
                      />
                    )}
                    {activeDiscussion?.isTeamWide ? (
                      <div className="parent-chat-mention-hint-row">
                        <button
                          type="button"
                          className="parent-chat-info-btn"
                          onClick={() => setDiscussionMentionInfoOpen((o) => !o)}
                          aria-expanded={discussionMentionInfoOpen}
                          aria-label="About mentions and notifications"
                        >
                          ⓘ
                        </button>
                        {discussionMentionInfoOpen ? (
                          <p className="parent-chat-muted parent-chat-mention-hint parent-chat-mention-hint--inline">
                            Team-wide: browser notifications are sent only to parents you @mention.
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {mentionedUserIds.length > 0 ? (
                      <div className="parent-chat-mention-chips" aria-label="Mentioned parents">
                        {mentionedUserIds.map((mid) => {
                          const p = directory.find((x) => x.uid === mid);
                          const label = p?.name || 'Parent';
                          return (
                            <span key={mid} className="parent-chat-mention-chip">
                              @{label}
                              <button type="button" onClick={() => removeMention(mid)} aria-label={`Remove ${label}`}>
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    ) : null}
                    <div className="parent-chat-composer-row">
                      <label className="parent-chat-attach-btn">
                        <input
                          type="file"
                          accept="image/*"
                          className="visually-hidden"
                          onChange={onDiscussionImagePick}
                        />
                        <span aria-hidden>📎</span>
                      </label>
                      <div className="parent-chat-mention-wrap" ref={mentionPanelRef}>
                        <button
                          type="button"
                          className="parent-chat-mention-toggle"
                          onClick={() => setMentionOpen((o) => !o)}
                          aria-expanded={mentionOpen}
                          aria-label="Mention a parent"
                        >
                          @
                        </button>
                        {mentionOpen ? (
                          <div className="parent-chat-mention-dropdown">
                            <input
                              type="search"
                              className="parent-chat-mention-search"
                              placeholder="Search parents…"
                              value={mentionSearch}
                              onChange={(e) => setMentionSearch(e.target.value)}
                              aria-label="Search parents to mention"
                            />
                            <ul className="parent-chat-mention-list" role="listbox">
                              {directoryLoading ? (
                                <li className="parent-chat-muted">Loading…</li>
                              ) : mentionFiltered.length === 0 ? (
                                <li className="parent-chat-muted">No matches</li>
                              ) : (
                                mentionFiltered.map((p) => (
                                  <li key={p.uid}>
                                    <button type="button" className="parent-chat-mention-pick" onClick={() => addDiscussionMention(p)}>
                                      {p.name}
                                    </button>
                                  </li>
                                ))
                              )}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                      <textarea
                        ref={forumInputRef}
                        id="discussion-composer"
                        rows={1}
                        value={discussionText}
                        onChange={(e) => setDiscussionText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendDiscussion(e);
                          }
                        }}
                        placeholder="Message…"
                        maxLength={8000}
                        className="parent-chat-input"
                        enterKeyHint="send"
                        autoComplete="off"
                        autoCorrect="on"
                      />
                      <button
                        type="submit"
                        className="parent-chat-send"
                        disabled={
                          discussionSending ||
                          !activeDiscussionId ||
                          (!discussionText.trim() && !discussionImageFile)
                        }
                        aria-label="Send"
                      >
                        {discussionSending ? '…' : '➤'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {subTab === 'dms' && (
        <div
          className={`parent-chat-dm-layout ${dmLayoutThreadOnly ? 'parent-chat-dm-layout--thread-only' : ''}`}
        >
          {showDmList && (
            <div
              className={`parent-chat-dm-sidebar${showNewDm ? ' parent-chat-dm-sidebar--picking' : ''}`}
            >
              <div className="parent-chat-dm-toolbar">
                <button type="button" className="parent-chat-btn-secondary" onClick={() => setShowNewDm((s) => !s)}>
                  {showNewDm ? 'Cancel' : '+ New'}
                </button>
              </div>
              {showNewDm && (
                <div className="parent-chat-new-dm">
                  <div className="parent-chat-new-dm-intro">
                    <span className="parent-chat-new-dm-label">Start a chat</span>
                    <p className="parent-chat-muted parent-chat-new-dm-hint">
                      Pick one parent for a direct message, or two or more for a group. Tap <strong>Open chat</strong>{' '}
                      when ready.
                    </p>
                    {directoryLoading ? <p className="parent-chat-muted">Loading parent list…</p> : null}
                    {!directoryLoading && directory.length > 0 ? (
                      <input
                        type="search"
                        className="parent-chat-dm-search"
                        placeholder="Search parents…"
                        value={dmDirectorySearch}
                        onChange={(e) => setDmDirectorySearch(e.target.value)}
                        aria-label="Search parents by name"
                        autoComplete="off"
                        enterKeyHint="search"
                      />
                    ) : null}
                  </div>
                  <div className="parent-chat-new-dm-checkboxes" role="list">
                    {!directoryLoading && directoryFiltered.length === 0 ? (
                      <p className="parent-chat-muted parent-chat-dm-search-empty">No names match your search.</p>
                    ) : (
                      directoryFiltered.map((p) => (
                        <label key={p.uid} className="parent-chat-dm-pick-row">
                          <input
                            type="checkbox"
                            className="parent-chat-dm-pick-checkbox"
                            checked={newDmPickIds.includes(p.uid)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewDmPickIds((prev) => [...prev, p.uid]);
                              } else {
                                setNewDmPickIds((prev) => prev.filter((x) => x !== p.uid));
                              }
                            }}
                          />
                          <span className="parent-chat-dm-pick-name">{p.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                  <div className="parent-chat-new-dm-actions">
                    {newDmPickIds.length > 0 ? (
                      <p className="parent-chat-new-dm-selection-summary" aria-live="polite">
                        {newDmPickIds.length === 1
                          ? '1 parent selected'
                          : `${newDmPickIds.length} parents selected — group chat`}
                      </p>
                    ) : (
                      <p className="parent-chat-new-dm-selection-summary parent-chat-new-dm-selection-summary--empty">
                        Select at least one parent.
                      </p>
                    )}
                    <button
                      type="button"
                      className="parent-chat-btn-primary parent-chat-new-dm-open"
                      onClick={openOrCreateDm}
                      disabled={newDmPickIds.length === 0}
                    >
                      {newDmPickIds.length >= 2 ? 'Start group chat' : 'Open chat'}
                    </button>
                  </div>
                </div>
              )}
              {!showNewDm &&
                (convLoading ? (
                  <p className="parent-chat-muted">Loading…</p>
                ) : conversations.length === 0 ? (
                  <p className="parent-chat-muted">No messages yet. Tap + New.</p>
                ) : (
                  <>
                    {activeDmConversations.length === 0 ? (
                      <p className="parent-chat-muted parent-chat-archive-hint">
                        No active conversations. Use <strong>Archived</strong> below to restore a chat, or tap + New.
                      </p>
                    ) : (
                      <ul className="parent-chat-conv-list">{activeDmConversations.map((c) => renderDmSidebarRow(c, false))}</ul>
                    )}
                    {archivedDmConversations.length > 0 ? (
                      <div className="parent-chat-archived-block">
                        <button
                          type="button"
                          className="parent-chat-archived-toggle"
                          onClick={() => setDmArchivedSectionOpen((o) => !o)}
                          aria-expanded={dmArchivedSectionOpen}
                        >
                          <span className="parent-chat-archived-chevron">{dmArchivedSectionOpen ? '▼' : '▶'}</span>
                          Archived ({archivedDmConversations.length})
                        </button>
                        {dmArchivedSectionOpen ? (
                          <ul className="parent-chat-conv-list parent-chat-conv-list--archived">
                            {archivedDmConversations.map((c) => renderDmSidebarRow(c, true))}
                          </ul>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ))}
            </div>
          )}

          {showDmThread && (
            <div className="parent-chat-dm-thread">
              <>
                  <div className="parent-chat-dm-thread-header">
                    {activeConvId ? (
                      <button
                        type="button"
                        className="parent-chat-back"
                        onClick={() => setActiveConvId(null)}
                        aria-label="Back to messages list"
                      >
                        ←
                      </button>
                    ) : null}
                    <div className="parent-chat-dm-thread-peer">
                      {!isGroupChat && otherPhoto ? (
                        <img src={otherPhoto} alt="" className="parent-chat-dm-thread-avatar" />
                      ) : (
                        <span className="parent-chat-avatar-placeholder sm">
                          {initialsFromName(isGroupChat ? 'Group' : otherName)}
                        </span>
                      )}
                      <h2 className="parent-chat-dm-title">
                        {isGroupChat ? groupTitle || 'Group chat' : otherName}
                      </h2>
                      {activeConvId ? (
                        <button
                          type="button"
                          className="parent-chat-thread-archive"
                          onClick={() =>
                            archivedDmIdSet.has(activeConvId)
                              ? void unarchiveDmConversation(activeConvId)
                              : void archiveDmConversation(activeConvId)
                          }
                        >
                          {archivedDmIdSet.has(activeConvId) ? 'Restore' : 'Archive'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {dmLoading ? (
                    <p className="parent-chat-muted">Loading…</p>
                  ) : (
                    <div className="parent-chat-stream-wrap parent-chat-stream-wrap--dm">
                      {showDmJump ? (
                        <button
                          type="button"
                          className="parent-chat-jump-latest"
                          onClick={() => scrollDmToBottom('smooth')}
                        >
                          Jump to latest
                        </button>
                      ) : null}
                      <div
                        ref={dmStreamRef}
                        className="parent-chat-stream parent-chat-stream-dm"
                        onScroll={onDmScroll}
                      >
                        {!dmNoMoreOlder && (dmLiveWindow.length >= DM_PAGE || dmOlderFetched.length > 0) ? (
                          <div className="parent-chat-load-older-wrap">
                            <button
                              type="button"
                              className="parent-chat-load-older"
                              onClick={() => void loadDmOlder()}
                              disabled={dmLoadingOlder}
                            >
                              {dmLoadingOlder ? 'Loading…' : 'Load older messages'}
                            </button>
                          </div>
                        ) : null}
                        {dmMessages.map((m, i) => {
                          const prevMsg = i > 0 ? dmMessages[i - 1] : null;
                          const nextMsg = i + 1 < dmMessages.length ? dmMessages[i + 1] : null;
                          const d = messageDate(m);
                          const prevD = prevMsg ? messageDate(prevMsg) : null;
                          const daySep =
                            d && (!prevD || !sameCalendarDay(prevD, d)) ? (
                              <div key={`dm-day-${m.id}`} className="parent-chat-day-sep" role="separator">
                                {dateSeparatorLabel(d)}
                              </div>
                            ) : null;
                          const gPrev = !!(prevMsg && messagesGroupTogether(prevMsg, m));
                          const gNext = !!(nextMsg && messagesGroupTogether(m, nextMsg));
                          return (
                            <React.Fragment key={m.id}>
                              {daySep}
                              <DmMessageRow
                                m={m}
                                uid={uid}
                                displayName={displayName}
                                myPhotoUrl={myPhotoUrl}
                                dmMessages={dmMessages}
                                isNarrow={isNarrow}
                                actionOpen={dmActionId === m.id}
                                onDesktopActivate={() =>
                                  setDmActionId((prev) => (prev === m.id ? null : m.id))
                                }
                                onReply={() => {
                                  setReplyToDm({ id: m.id, authorName: m.authorName || 'Parent' });
                                  setDmActionId(null);
                                }}
                                onRequestDelete={() => setPendingDelete({ channel: 'dm', msg: m })}
                                onMobileMenuOpen={
                                  isNarrow ? () => setMobileMsgMenu({ type: 'dm', msg: m }) : undefined
                                }
                                renderBubbleContent={renderBubbleContent}
                                renderReplyContext={renderReplyContext}
                                timeShort={msgTimeShort(m)}
                                groupWithPrev={gPrev}
                                groupWithNext={gNext}
                              />
                            </React.Fragment>
                          );
                        })}
                        <div ref={dmEndRef} />
                      </div>
                    </div>
                  )}
                  <form className="parent-chat-composer parent-chat-composer--dm" onSubmit={sendDm}>
                    {dmImagePreview && (
                      <div className="parent-chat-pending-img">
                        <img src={dmImagePreview} alt="" />
                        <button type="button" className="parent-chat-pending-remove" onClick={clearDmImage} aria-label="Remove">
                          ×
                        </button>
                      </div>
                    )}
                    {replyToDm && (
                      <ReplyComposerPreview
                        authorLabel={replyToDm.authorName}
                        quotedMessage={dmMessages.find((x) => x.id === replyToDm.id)}
                        onDismiss={() => setReplyToDm(null)}
                      />
                    )}
                    {isGroupChat ? (
                      <div className="parent-chat-mention-hint-row">
                        <button
                          type="button"
                          className="parent-chat-info-btn"
                          onClick={() => setDmGroupMentionInfoOpen((o) => !o)}
                          aria-expanded={dmGroupMentionInfoOpen}
                          aria-label="About group mentions"
                        >
                          ⓘ
                        </button>
                        {dmGroupMentionInfoOpen ? (
                          <p className="parent-chat-muted parent-chat-mention-hint parent-chat-mention-hint--inline">
                            @mention to notify only that person; leave empty to notify everyone in the chat.
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {mentionedUserIds.length > 0 ? (
                      <div className="parent-chat-mention-chips" aria-label="Mentioned parents">
                        {mentionedUserIds.map((mid) => {
                          const p =
                            dmMentionDirectory.find((x) => x.uid === mid) ||
                            directory.find((x) => x.uid === mid);
                          const label = p?.name || 'Parent';
                          return (
                            <span key={mid} className="parent-chat-mention-chip">
                              @{label}
                              <button type="button" onClick={() => removeMention(mid)} aria-label={`Remove ${label}`}>
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    ) : null}
                    <div className="parent-chat-composer-row">
                      <label className="parent-chat-attach-btn">
                        <input type="file" accept="image/*" className="visually-hidden" onChange={onDmImagePick} />
                        <span aria-hidden>📎</span>
                      </label>
                      <div className="parent-chat-mention-wrap" ref={mentionPanelRef}>
                        <button
                          type="button"
                          className="parent-chat-mention-toggle"
                          onClick={() => setMentionOpen((o) => !o)}
                          aria-expanded={mentionOpen}
                          aria-label="Mention someone in this chat"
                        >
                          @
                        </button>
                        {mentionOpen ? (
                          <div className="parent-chat-mention-dropdown">
                            <input
                              type="search"
                              className="parent-chat-mention-search"
                              placeholder="Search…"
                              value={mentionSearch}
                              onChange={(e) => setMentionSearch(e.target.value)}
                              aria-label="Search people in this chat"
                            />
                            <ul className="parent-chat-mention-list" role="listbox">
                              {dmMentionDirectory.length === 0 ? (
                                <li className="parent-chat-muted">Open a conversation to mention someone.</li>
                              ) : dmMentionFiltered.length === 0 ? (
                                <li className="parent-chat-muted">No matches</li>
                              ) : (
                                dmMentionFiltered.map((p) => (
                                  <li key={p.uid}>
                                    <button
                                      type="button"
                                      className="parent-chat-mention-pick"
                                      onClick={() => addDmMention(p)}
                                    >
                                      {p.name}
                                    </button>
                                  </li>
                                ))
                              )}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                      <textarea
                        ref={dmInputRef}
                        id="dm-composer"
                        rows={1}
                        value={dmText}
                        onChange={(e) => setDmText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendDm(e);
                          }
                        }}
                        placeholder="Message…"
                        maxLength={8000}
                        className="parent-chat-input"
                        enterKeyHint="send"
                        autoComplete="off"
                        autoCorrect="on"
                      />
                      <button
                        type="submit"
                        className="parent-chat-send"
                        disabled={dmSending || (!dmText.trim() && !dmImageFile)}
                        aria-label="Send"
                      >
                        {dmSending ? '…' : '➤'}
                      </button>
                    </div>
                  </form>
                </>
            </div>
          )}
        </div>
      )}

      {pendingDelete ? (
        <div
          className="parent-chat-modal-backdrop"
          role="presentation"
          onClick={() => setPendingDelete(null)}
        >
          <div
            className="parent-chat-modal"
            role="alertdialog"
            aria-labelledby="parent-chat-del-title"
            aria-describedby="parent-chat-del-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="parent-chat-del-title" className="parent-chat-modal-title">
              Delete this message?
            </h2>
            <p id="parent-chat-del-desc" className="parent-chat-modal-text">
              This cannot be undone.
            </p>
            <div className="parent-chat-modal-actions">
              <button type="button" className="parent-chat-btn-secondary" onClick={() => setPendingDelete(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="parent-chat-btn-danger"
                onClick={() => {
                  const p = pendingDelete;
                  setPendingDelete(null);
                  if (p.channel === 'discussion') void removeDiscussionMessage(p.msg);
                  else void removeDmMessage(p.msg);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {mobileMsgMenu && (
        <div
          className="parent-chat-mobile-sheet-backdrop"
          role="presentation"
          onClick={() => setMobileMsgMenu(null)}
        >
          <div
            className="parent-chat-mobile-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Message actions"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="parent-chat-mobile-sheet-item" onClick={handleMobileSheetReply}>
              <span className="parent-chat-mobile-sheet-icon" aria-hidden>
                ↩
              </span>
              Reply
            </button>
            {mobileMsgMenu.msg.authorId === uid ? (
              <button
                type="button"
                className="parent-chat-mobile-sheet-item parent-chat-mobile-sheet-item--danger"
                onClick={handleMobileSheetDelete}
              >
                <span className="parent-chat-mobile-sheet-icon" aria-hidden>
                  🗑
                </span>
                Delete
              </button>
            ) : null}
            <button
              type="button"
              className="parent-chat-mobile-sheet-cancel"
              onClick={() => setMobileMsgMenu(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
